import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { GoogleSignIn as GoogleAuth } from '@capawesome/capacitor-google-sign-in'
import axios from 'axios'
import './index.css'
import 'leaflet/dist/leaflet.css'
import './i18n'
import App from './App.tsx'

// Configure global axios defaults for native mobile compatibility
let globalApiUrl = import.meta.env.VITE_API_URL || '';
if (typeof window !== 'undefined') {
    const override = localStorage.getItem('CUSTOM_API_URL');
    if (override) {
        globalApiUrl = override;
    }
}
const isNative = typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();
if (!isNative) {
    globalApiUrl = '/api';
} else if (!globalApiUrl) {
    globalApiUrl = 'https://www.petsmartmonitor.com/api';
}
if (globalApiUrl && globalApiUrl.startsWith('http')) {
    let base = globalApiUrl;
    if (base.endsWith('/api')) {
        base = base.substring(0, base.length - 4);
    } else if (base.endsWith('/api/')) {
        base = base.substring(0, base.length - 5);
    }
    axios.defaults.baseURL = base;
    axios.defaults.withCredentials = true;
}

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '84094836290-6fvdpv8ic12252t99m7ho4sflmv8usup.apps.googleusercontent.com';

// Detect Capacitor platform and apply device-specific safe area overrides
if (typeof window !== 'undefined') {
    const win = window as any;
    const isCapacitor = !!win.Capacitor;
    if (isCapacitor) {
        try {
            const platform = win.Capacitor.getPlatform();
            let googleClientId = clientId;
            if (platform === 'ios') {
                googleClientId = '84094836290-7hbruk5ce6ii865gq4ogvs0uf99fg6bi.apps.googleusercontent.com';
            }
            GoogleAuth.initialize({
                clientId: googleClientId,
                scopes: ['profile', 'email'],
            });
            console.log(`[Google Auth] Native plugin initialized successfully for ${platform}`);
        } catch (e) {
            console.error('[Google Auth] Native plugin initialization failed:', e);
        }
    }
    if (isCapacitor && win.Capacitor.getPlatform() === 'ios') {
        const doc = document.documentElement;

        /**
         * getSafeAreaFallback: derive notch height from logical screen dimensions.
         * screen.height in a WKWebView is in CSS px (points), not physical pixels.
         * iPhone 16 Pro Max: screen.height = 956pt  → safe area top = 62pt
         * iPhone 14/15 Pro Max, 16 Pro: ~932pt       → 59pt
         * iPhone 12/13/14/15, Pro: ~844pt             → 47pt
         * iPhone SE (no notch): ~667pt                → 20pt
         */
        const getSafeAreaFallback = (): number => {
            const h = window.screen.height;
            if (h >= 950) return 62;
            if (h >= 920) return 59;
            if (h >= 800) return 47;
            return 20;
        };

        // Apply immediately (before first paint) so no layout shift occurs
        const fallback = getSafeAreaFallback();
        doc.classList.add('platform-ios-capacitor');
        doc.style.setProperty('--ios-safe-area-top', `${fallback}px`);
        console.log(`[iOS Native Layout] Initial safe area: ${fallback}px (screen.height=${window.screen.height})`);

        // After DOM is ready, try to measure env(safe-area-inset-top) more accurately
        const refineFromEnv = () => {
            const div = document.createElement('div');
            div.style.cssText = 'position:fixed;top:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;';
            document.body.appendChild(div);
            const measured = parseFloat(window.getComputedStyle(div).height || '0');
            document.body.removeChild(div);

            if (measured > 0 && measured !== fallback) {
                doc.style.setProperty('--ios-safe-area-top', `${measured}px`);
                console.log(`[iOS Native Layout] Refined safe area to ${measured}px from env()`);
            }
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', refineFromEnv, { once: true });
        } else {
            refineFromEnv();
        }
    }
}



import { Suspense } from 'react'

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GoogleOAuthProvider clientId={clientId}>
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>}>
        <App />
      </Suspense>
    </GoogleOAuthProvider>
  </StrictMode>,
)
